'use client';

import { useId, useState, useTransition } from 'react';

type Status = 'idle' | 'success' | 'error';

export function ContactForm() {
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [message, setMessage] = useState('');
  const [hp,      setHp]      = useState('');
  const [status,  setStatus]  = useState<Status>('idle');
  const [error,   setError]   = useState('');
  const [pending, startTransition] = useTransition();
  const nameId    = useId();
  const emailId   = useId();
  const messageId = useId();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes('@')) {
      setError('Please enter a valid email.');
      setStatus('error');
      return;
    }
    if (!message.trim()) {
      setError('Please write a short message.');
      setStatus('error');
      return;
    }
    startTransition(async () => {
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, message, website: hp }),
        });
        const json = await res.json();
        if (json.ok) {
          setStatus('success');
          setName(''); setEmail(''); setMessage('');
        } else {
          setError(json.error || 'Could not send. Please call or text (351) 322-5409.');
          setStatus('error');
        }
      } catch {
        setError('Could not send. Please call or text (351) 322-5409.');
        setStatus('error');
      }
    });
  }

  if (status === 'success') {
    return (
      <div className="border-l-2 border-gold-soft bg-ink/5 p-6">
        <p className="text-xs uppercase tracking-[0.14em] text-ink/65 mb-2 font-medium">Message Sent</p>
        <p className="text-ink/75 text-sm leading-relaxed">
          Thanks. We got your message and will reply to the email you gave us.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 relative">
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>Leave this field empty<input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} /></label>
      </div>
      <div>
        <label htmlFor={nameId} className="block text-xs uppercase tracking-[0.12em] text-muted-strong mb-2">Name</label>
        <input
          id={nameId}
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setStatus('idle'); }}
          className="w-full bg-transparent border-b border-ink/20 py-3 text-ink focus:border-ink focus:outline-none transition-colors"
        />
      </div>
      <div>
        <label htmlFor={emailId} className="block text-xs uppercase tracking-[0.12em] text-muted-strong mb-2">Email *</label>
        <input
          id={emailId}
          type="email"
          required
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
          className="w-full bg-transparent border-b border-ink/20 py-3 text-ink focus:border-ink focus:outline-none transition-colors"
        />
      </div>
      <div>
        <label htmlFor={messageId} className="block text-xs uppercase tracking-[0.12em] text-muted-strong mb-2">How can we help? *</label>
        <textarea
          id={messageId}
          rows={5}
          required
          value={message}
          onChange={(e) => { setMessage(e.target.value); setStatus('idle'); }}
          className="w-full bg-transparent border-b border-ink/20 py-3 text-ink focus:border-ink focus:outline-none transition-colors resize-none"
        />
      </div>
      <button
        type="submit"
        disabled={pending}
        className="bg-plum text-white px-12 py-4 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors mt-6 disabled:opacity-60 rounded-sm"
      >
        {pending ? 'Sending…' : 'Send Message'}
      </button>
      {status === 'error' && (
        <p className="text-ink text-sm mt-3 leading-relaxed" role="alert">{error}</p>
      )}
    </form>
  );
}
